import { useEffect } from "react";
import { useDialerContext } from "../contexts/DialerContext";
import { useSip } from "../contexts/SipContext";
import { useIntegratedDialer } from "../contexts/IntegratedDialerContext";

/**
 * ActiveCallWarning Component
 *
 * Warns users before closing/refreshing the tab when they have an active call
 * Monitors call state from multiple contexts and prevents accidental disconnection
 */
const ActiveCallWarning = () => {
  const { currentCallInfo, agentStatus } = useDialerContext() || {};
  const { isConnected: sipConnected, activeCall: sipCall } = useSip() || {};
  const { activeCall: integratedCall } = useIntegratedDialer() || {};

  const hasActiveCall = Boolean(
    (currentCallInfo && currentCallInfo.status === "in-progress") ||
    agentStatus === "on_call" ||
    (sipConnected && sipCall) ||
    (integratedCall && integratedCall.status === "active"),
  );

  // Listeners exist only while a call is live: a page with no call keeps the
  // back/forward cache, and "pagehide" replaces the deprecated "unload".
  useEffect(() => {
    if (!hasActiveCall) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue =
        "You have an active call. Are you sure you want to leave? This will disconnect the call.";
      return event.returnValue;
    };

    const handlePageHide = (event) => {
      // Kept in the back/forward cache: the page (and the call) may come back.
      if (event.persisted) return;
      // Best effort: the browser may not finish either request.
      try {
        if (sipCall && typeof sipCall.hangup === "function") {
          sipCall.hangup();
        }
        if (currentCallInfo && currentCallInfo.id) {
          navigator.sendBeacon(
            `/api/calls/${currentCallInfo.id}/hangup`,
            JSON.stringify({ reason: "page_unload" }),
          );
        }
      } catch {
        // Nothing to report to: the page is going away.
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [hasActiveCall, currentCallInfo, sipCall]);

  // This component doesn't render anything visible
  return null;
};

export default ActiveCallWarning;
